import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  ContentCopy,
  BarChart,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import {
  useGetSeasonsQuery,
  useCreateSeasonMutation,
  useUpdateSeasonMutation,
  useDeleteSeasonsMutation,
  useDuplicateSeasonMutation,
  useGetSeasonStatsQuery,
} from 'features/apiSlice';
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import FlexBetween from 'components/FlexBetween';
import DeleteConfirm from 'components/DeleteConfirm';
import SeasonFormDialog from './SeasonFormDialog';
import DuplicateSeasonDialog from './DuplicateSeasonDialog';
import SeasonStatsDialog from './SeasonStatsDialog';
import SeasonList from './SeasonList';


const STATUS_COLOR = {
  draft: 'default',
  active: 'primary',
  published: 'success',
  closed: 'error',
};

const ScheduleSeasons = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "ScheduleSeasons", "create", false);
  const canUpdate = hasRight(permissionMap, "ScheduleSeasons", "update", false);
  const canDelete = hasRight(permissionMap, "ScheduleSeasons", "delete", false);
  
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [rowSelectionModel, setRowSelectionModel] = useState({ 
    type: 'include', 
    ids: new Set() 
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const [formDialog, setFormDialog] = useState({ open: false, data: null });
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    sourceId: null,
    sourceCode: '',
  });
  const [statsDialog, setStatsDialog] = useState({
    open: false,
    seasonId: null,
    seasonName: '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteType, setDeleteType] = useState('bulk');
  const [idToDelete, setIdToDelete] = useState(null);

  const queryParams = useMemo(() => ({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  }), [paginationModel]);

  const { data, isLoading, refetch } = useGetSeasonsQuery(queryParams);
  const seasons = useMemo(() => data?.data || [], [data]);
  const total = data?.total || 0;

  const { data: statsData } = useGetSeasonStatsQuery(statsDialog.seasonId, {
    skip: !statsDialog.seasonId,
  });

  const [createSeason] = useCreateSeasonMutation();
  const [updateSeason] = useUpdateSeasonMutation();
  const [deleteSeasons] = useDeleteSeasonsMutation();
  const [duplicateSeason] = useDuplicateSeasonMutation();

  const handleSave = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      if (formDialog.data) {
        await updateSeason({ id: formDialog.data._id, ...values }).unwrap();
        toast.success('Season updated.');
      } else {
        await createSeason(values).unwrap();
        toast.success('Season created.');
      }
      resetForm();
      setFormDialog({ open: false, data: null });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }, [
    formDialog.data, 
    createSeason, 
    updateSeason, 
    refetch,
  ]);

  const handleDuplicate = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      const result = await duplicateSeason({ id: duplicateDialog.sourceId, ...values }).unwrap();
      toast.success(`Duplicated: ${result.copiedFlights} flights copied.`);
      resetForm();
      setDuplicateDialog({ open: false, sourceId: null, sourceCode: '' });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Duplicate failed.');
    } finally {
      setSubmitting(false);
    }
  }, [
    duplicateDialog.sourceId, 
    duplicateSeason, 
    refetch,
  ]);

  const handleDeleteClick = useCallback((id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDelete(true);
  }, []);

  const handleBulkDeleteClick = () => {
    setIdToDelete(null);
    setDeleteType('bulk');
    setConfirmDelete(true);
  };

  const handleDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await deleteSeasons([idToDelete]).unwrap();
        toast.success('Season deleted.');
        setIdToDelete(null);
      } else {
        const ids = getSelectedIds(rowSelectionModel, seasons);
        if (!ids.length) return;
        await deleteSeasons(ids).unwrap();
        toast.success(`${ids.length} season(s) deleted.`);
        setRowSelectionModel({ type: 'include', ids: new Set() });
      }
      setConfirmDelete(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed.');
    }
  }, [
    deleteType,
    idToDelete,
    rowSelectionModel,
    seasons,
    deleteSeasons,
    refetch,
  ]);

  const columns = useMemo(() => [
    {
      field: 'seasonCode',
      headerName: 'Code',
      width: 100,
    },
    { 
      field: 'seasonName', 
      headerName: 'SeasonName', 
      flex: 1, 
      minWidth: 180,
    },
    {
      field: 'iataCode',
      headerName: 'Type',
      width: 80,
      renderCell: ({ value }) => (
        <Chip
          label={value === 'S' ? 'Summer' : 'Winter'}
          size="small"
          color={value === 'S' ? 'warning' : 'info'}
          variant="outlined"
        />
      ),
    },
    { field: 'year', headerName: 'Year', width: 80 },
    {
      field: 'startDate',
      headerName: 'Start',
      width: 110,
      renderCell: ({ value }) => value ? format(new Date(value), 'dd MMM yy') : '-',
    },
    {
      field: 'endDate',
      headerName: 'End',
      width: 110,
      renderCell: ({ value }) => value ? format(new Date(value), 'dd MMM yy') : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={STATUS_COLOR[value] || 'default'} />
      ),
    },
    { field: 'baseAirline', headerName: 'Airline', width: 80 },
    {
      field: 'actions',
      headerName: 'Action',
      width: 155,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          sx={{ width: "100%", height: "100%" }}
        >
          <Tooltip title="Stats" arrow>
            <IconButton
              size="small"
              onClick={() => setStatsDialog({ open: true, seasonId: row._id, seasonName: row.seasonName })}
            >
              <BarChart fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate season" arrow>
            <IconButton
              size="small"
              onClick={() => setDuplicateDialog({ open: true, sourceId: row._id, sourceCode: row.seasonCode })}
              disabled={!canCreate}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={() => setFormDialog({ open: true, data: row })}
              disabled={!canUpdate}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(row._id)}
              disabled={!canDelete}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [
    canCreate, 
    canUpdate, 
    canDelete, 
    handleDeleteClick
  ]);

  const hasSelection = isAnyRowSelected(rowSelectionModel, seasons);
  const isMobile    = useMediaQuery(theme.breakpoints.down('sm'));


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Schedule Seasons" 
          subtitle="List of Seasons" 
        />
        <Box display="flex" alignItems="center" gap={1}>
          {hasSelection && (
            <Tooltip title="Delete selected" arrow>
              <span>
                <StyledButton
                  disabled={!canDelete}
                  onClick={handleBulkDeleteClick}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Create" arrow>
            <span>
              <StyledButton 
                disabled={!canCreate} 
                onClick={() => setFormDialog({ open: true, data: null })}
              >
                <AddCircleOutline />
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </FlexBetween>
      
      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box 
          height={LAYOUT_HEIGHT} 
          sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
        >
          {isMobile ? (
            <SeasonList
              seasons={seasons}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onEdit={(row) => setFormDialog({ open: true, data: row })}
              onDelete={handleDeleteClick}
              onDuplicate={(row) => setDuplicateDialog({ open: true, sourceId: row._id, sourceCode: row.seasonCode })}
              onStats={(row) => setStatsDialog({ open: true, seasonId: row._id, seasonName: row.seasonName })}
            />
          ) : (
            /* ── Desktop DataGrid ── */        
            <DataGridComponent
              rows={seasons}
              columns={columns}
              loading={isLoading}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              props={{
                rowCount: total,
                paginationMode: 'server',
                paginationModel,
                onPaginationModelChange: setPaginationModel,
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                rowSelectionModel,
                onRowSelectionModelChange: setRowSelectionModel,
                sx: {
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: theme.palette.background.alt },
                },
              }}
            />         
          )}
        </Box>
      </Box>

      <SeasonFormDialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, data: null })}
        data={formDialog.data}
        onSave={handleSave}
      />

      <DuplicateSeasonDialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, sourceId: null, sourceCode: '' })}
        sourceCode={duplicateDialog.sourceCode}
        onDuplicate={handleDuplicate}
      />

      <SeasonStatsDialog
        open={statsDialog.open}
        onClose={() => setStatsDialog({ open: false, seasonId: null, seasonName: '' })}
        seasonName={statsDialog.seasonName}
        statsData={statsData}
      />

      <DeleteConfirm
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default ScheduleSeasons;
